/**
 * Every type list in the workspace with its types, ready for pickers and the
 * pattern editor. Built-in lists read their own catalogs (work types from the
 * work type catalog, launch and investment types from the item taxonomy store);
 * custom lists read custom_types. One place answers "what types are in this list".
 */

import { useEffect, useMemo } from 'react';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useTypePatternsStore } from '../../stores/prioritization/typePatternsStore';
import { useItemTaxonomyStore } from '../../stores/prioritization/itemTaxonomyStore';
import { useWorkTypeCatalog } from './useWorkTypeCatalog';
import type { WorkTypeRecipeFacts } from '../../lib/typePatterns';
import type { TypeList, TypeOption } from '../../types/prioritization/typePatterns';

export interface TypeListWithOptions extends TypeList {
  options: TypeOption[];
}

export function useTypeListPickers(workspaceIdArg?: string | null) {
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspaceId = workspaceIdArg ?? currentWorkspaceId ?? null;

  const lists = useTypePatternsStore((s) => s.lists);
  const customTypes = useTypePatternsStore((s) => s.customTypes);
  const patterns = useTypePatternsStore((s) => s.patterns);
  const loading = useTypePatternsStore((s) => s.loading);
  const error = useTypePatternsStore((s) => s.error);
  const load = useTypePatternsStore((s) => s.load);

  const workTypes = useWorkTypeCatalog(workspaceId);
  const launchTypes = useItemTaxonomyStore((s) => s.launch_type.items);
  const investmentTypes = useItemTaxonomyStore((s) => s.investment_type.items);
  const fetchTaxonomy = useItemTaxonomyStore((s) => s.fetch);

  useEffect(() => {
    if (!workspaceId) return;
    void load(workspaceId);
    void fetchTaxonomy('launch_type', workspaceId);
    void fetchTaxonomy('investment_type', workspaceId);
  }, [workspaceId, load, fetchTaxonomy]);

  const withOptions: TypeListWithOptions[] = useMemo(
    () =>
      lists.map((list) => {
        let options: TypeOption[];
        if (list.key === 'work_type') {
          options = workTypes.options.map((o) => ({
            id: null,
            value: o.value,
            label: o.label,
            description: (o as { description?: string | null }).description ?? null,
            isActive: (o as { active?: boolean }).active !== false,
          }));
        } else if (list.key === 'launch_type' || list.key === 'investment_type') {
          const src = list.key === 'launch_type' ? launchTypes : investmentTypes;
          options = src.map((t) => ({ id: null, value: t.value, label: t.label, description: t.description, isActive: t.isActive }));
        } else {
          options = customTypes
            .filter((t) => t.listId === list.id)
            .map(({ listId: _l, ...t }) => t);
        }
        return { ...list, options };
      }),
    [lists, customTypes, workTypes.options, launchTypes, investmentTypes],
  );

  const typeLabel = (listKey: string, value: string): string =>
    withOptions.find((l) => l.key === listKey)?.options.find((o) => o.value === value)?.label ?? value;

  /** Approach and effort multiplier stay on the work type's own row (canon 111). */
  const workTypeFacts = (value: string): WorkTypeRecipeFacts | null => {
    const o = workTypes.options.find((x) => x.value === value) as
      | { approachKind?: string; multiplier?: number }
      | undefined;
    return o ? { approach: o.approachKind ?? null, effortMultiplier: o.multiplier ?? null } : null;
  };

  return {
    workspaceId,
    lists: withOptions,
    patterns,
    loading,
    error,
    typeLabel,
    workTypeFacts,
    reload: () => (workspaceId ? load(workspaceId, { force: true }) : Promise.resolve()),
  };
}
